/**
 * Sistema de monitoreo en tiempo real para el proxy de imágenes
 * Rastrea métricas de performance y detecta problemas automáticamente
 */

export interface ImageProxyMetrics {
  proxySuccesses: number;
  proxyFailures: number;
  directLoads: number;
  totalImages: number;
  avgLoadTime: number;
  egressSaved: number;
  lastUpdate: number;
}

export interface ImageLoadEvent {
  url: string;
  wasProxied: boolean;
  loadTime: number;
  success: boolean;
  timestamp: number;
  errorType?: string;
}

class ImageProxyMonitor {
  private metrics: ImageProxyMetrics;
  private recentEvents: ImageLoadEvent[] = [];
  private isEnabled = false;
  private reportInterval: number | null = null;

  constructor() {
    this.metrics = {
      proxySuccesses: 0,
      proxyFailures: 0,
      directLoads: 0,
      totalImages: 0,
      avgLoadTime: 0,
      egressSaved: 0,
      lastUpdate: Date.now()
    };
    
    // Solo habilitar en desarrollo
    this.isEnabled = import.meta.env.DEV;
    
    if (this.isEnabled) {
      this.startPeriodicReporting();
    }
  }

  /**
   * Registra un evento de carga de imagen
   */
  recordImageLoad(event: ImageLoadEvent) {
    if (!this.isEnabled) return;

    this.recentEvents.push(event);
    
    // Mantener solo los últimos 100 eventos
    if (this.recentEvents.length > 100) {
      this.recentEvents = this.recentEvents.slice(-100);
    }

    // Actualizar métricas
    this.updateMetrics(event);
  }

  /**
   * Actualiza las métricas basadas en un evento
   */
  private updateMetrics(event: ImageLoadEvent) {
    this.metrics.totalImages++;
    
    if (event.wasProxied) {
      if (event.success) {
        this.metrics.proxySuccesses++;
        // Estimar egress ahorrado: ~500KB promedio por imagen
        this.metrics.egressSaved += 0.5;
      } else {
        this.metrics.proxyFailures++;
      }
    } else {
      this.metrics.directLoads++;
    }

    // Actualizar tiempo promedio de carga
    const loadTimes = this.recentEvents.map(e => e.loadTime).filter(t => t > 0);
    if (loadTimes.length > 0) {
      this.metrics.avgLoadTime = loadTimes.reduce((a, b) => a + b, 0) / loadTimes.length;
    }

    this.metrics.lastUpdate = Date.now();
  }

  /**
   * Obtiene las métricas actuales
   */
  getMetrics(): ImageProxyMetrics {
    return { ...this.metrics };
  }

  /**
   * Obtiene estadísticas detalladas
   */
  getDetailedStats() {
    const recent = this.recentEvents.slice(-20); // Últimos 20 eventos
    const proxySuccessRate = this.metrics.totalImages > 0 
      ? (this.metrics.proxySuccesses / this.metrics.totalImages) * 100 
      : 0;

    const directLoadRate = this.metrics.totalImages > 0
      ? (this.metrics.directLoads / this.metrics.totalImages) * 100
      : 0;

    return {
      metrics: this.metrics,
      recentEvents: recent,
      proxySuccessRate: Math.round(proxySuccessRate * 100) / 100,
      directLoadRate: Math.round(directLoadRate * 100) / 100,
      egressSavedMB: Math.round(this.metrics.egressSaved * 100) / 100,
      isHealthy: proxySuccessRate > 85 && directLoadRate < 15
    };
  }

  /**
   * Reporta estadísticas en consola cada 5 minutos
   */
  private startPeriodicReporting() {
    this.reportInterval = window.setInterval(() => {
      const stats = this.getDetailedStats();
      
      if (stats.metrics.totalImages > 0) {
        console.group('🖼️ Image Proxy Monitor - Stats');
        console.log(`📊 Total Images: ${stats.metrics.totalImages}`);
        console.log(`✅ Proxy Success Rate: ${stats.proxySuccessRate}%`);
        console.log(`🔗 Direct Load Rate: ${stats.directLoadRate}%`);
        console.log(`⚡ Avg Load Time: ${Math.round(stats.metrics.avgLoadTime)}ms`);
        console.log(`💾 Egress Saved: ~${stats.egressSavedMB}MB`);
        console.log(`🔋 Health Status: ${stats.isHealthy ? '✅ Healthy' : '⚠️ Needs Attention'}`);
        
        // Alertas automáticas
        if (!stats.isHealthy) {
          console.warn('⚠️ ALERT: Proxy performance below optimal thresholds!');
          if (stats.directLoadRate > 15) {
            console.warn('• Too many direct loads detected');
          }
          if (stats.proxySuccessRate < 85) {
            console.warn('• Proxy success rate is low');
          }
        }
        
        console.groupEnd();
      }
    }, 5 * 60 * 1000); // 5 minutos
  }

  /**
   * Para el monitoreo
   */
  stop() {
    if (this.reportInterval) {
      clearInterval(this.reportInterval);
      this.reportInterval = null;
    }
  }

  /**
   * Reinicia las métricas
   */
  reset() {
    this.metrics = {
      proxySuccesses: 0,
      proxyFailures: 0,
      directLoads: 0,
      totalImages: 0,
      avgLoadTime: 0,
      egressSaved: 0,
      lastUpdate: Date.now()
    };
    this.recentEvents = [];
  }
}

// Instancia singleton del monitor
const imageProxyMonitor = new ImageProxyMonitor();

// Función para obtener el monitor desde otros archivos
export function getImageProxyMonitor(): ImageProxyMonitor {
  return imageProxyMonitor;
}

// Export por defecto
export default imageProxyMonitor;